import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { ProductFormCard, ProductFormData } from '@/components/seller/products/ProductFormCard';
import { sellerService } from '@/services/seller';
import { imageService } from '@/services/image';
import { useToast } from '@/components/common/Toast';
import { supabase } from '@/lib/supabase';

export function DesktopProductRegisterPage() {
  const navigate = useNavigate();
  const { ToastContainer, showToast } = useToast();

  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductFormData[]>([
    {
      name: '',
      description: '',
      category: '',
      originalPrice: 0,
      stock: 20,
      imageFile: null,
      imageUrl: undefined
    }
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const initializePage = async () => {
      try {
        setLoading(true);
        
        // 판매자 레스토랑 ID 가져오기
        const restaurantResult = await sellerService.getSellerRestaurantId();
        if (!restaurantResult.success || !restaurantResult.restaurantId) {
          showToast(restaurantResult.error || '레스토랑 정보를 찾을 수 없습니다');
          return;
        }
        
        setRestaurantId(restaurantResult.restaurantId);
        
      } catch (error) {
        console.error('Initialize page error:', error);
        showToast('페이지 초기화 중 오류가 발생했습니다');
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, [showToast]);

  const handleProductChange = (index: number, product: ProductFormData) => {
    setProducts(prev => prev.map((p, i) => i === index ? product : p));
  };

  const handleAddProduct = () => {
    setProducts(prev => [...prev, {
      name: '',
      description: '',
      category: '',
      originalPrice: 0,
      stock: 20,
      imageFile: null,
      imageUrl: undefined
    }]);
  };

  const handleRemoveProduct = (index: number) => {
    if (products.length <= 1) {
      showToast('최소 1개의 상품은 필요합니다');
      return;
    }

    setProducts(prev => prev.filter((_, i) => i !== index));
  };

  const validateProducts = (): boolean => {
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      
      if (!product.name.trim()) {
        showToast(`${i + 1}번째 상품의 이름을 입력해주세요`);
        return false;
      }
      
      if (product.originalPrice <= 0) {
        showToast(`${i + 1}번째 상품의 가격을 입력해주세요`);
        return false;
      }
      
      if (product.stock <= 0) {
        showToast(`${i + 1}번째 상품의 재고를 입력해주세요`);
        return false;
      }
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!restaurantId || !validateProducts()) return;
    
    try {
      setSaving(true);
      
      // 1. 먼저 상품 정보를 DB에 저장하여 productId 획득
      const productsToCreate = products.map(product => ({
        name: product.name.trim(),
        description: product.description?.trim() || undefined,
        category: product.category?.trim() || undefined,
        originalPrice: product.originalPrice,
        stock: product.stock,
        imageUrl: undefined // 나중에 업데이트할 예정
      }));

      const result = await sellerService.createProducts(restaurantId, productsToCreate);
      
      if (!result.success || !result.productIds) {
        throw new Error(result.error || '상품 등록에 실패했습니다');
      }

      // 2. 각 상품에 이미지 업로드 (생성된 productIds를 정확히 매칭)
      const imageUploadPromises = products.map(async (product, index) => {
        if (product.imageFile && result.productIds?.[index]) {
          const productId = result.productIds[index]; // 정확한 순서 매칭
          const uploadResult = await imageService.uploadProductImage(
            product.imageFile,
            productId
          );

          if (!uploadResult.success || !uploadResult.url) {
            throw new Error(uploadResult.error || '이미지 업로드에 실패했습니다');
          }

          const { error: updateError } = await supabase
            .from('products')
            .update({ image_url: uploadResult.url })
            .eq('id', productId);

          if (updateError) {
            throw new Error(updateError.message || '상품 이미지 URL 업데이트에 실패했습니다');
          }
        }
      });

      await Promise.all(imageUploadPromises);
      
      showToast('상품이 성공적으로 등록되었습니다');
      // 3초 후 내 가게로 이동
      setTimeout(() => {
        navigate('/seller/store');
      }, 3000);
      
    } catch (error) {
      console.error('Save products error:', error);
      showToast(error instanceof Error ? error.message : '상품 등록 중 오류가 발생했습니다');
    } finally {
      setSaving(false);
    }
  };

  const canSave = products.every(p => 
    p.name.trim() && 
    p.originalPrice > 0 && 
    p.stock > 0
  ) && !saving;

  if (loading) {
    return (
      <DesktopSellerPageContainer>
        <div className="flex items-center justify-center min-h-[600px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
            <p className="text-[18px] text-gray-600">페이지를 불러오는 중...</p>
          </div>
        </div>
      </DesktopSellerPageContainer>
    );
  }

  return (
    <DesktopSellerPageContainer>
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/seller/store')}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-[32px] font-bold text-gray-800">상품 등록</h1>
          </div>

          {/* 안내 메시지 */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-blue-500 mt-0.5 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-[16px] text-blue-700">
                <p className="font-semibold mb-3">상품 등록 안내</p>
                <ul className="space-y-2 text-[15px]">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>원하는 만큼 상품을 한 번에 등록할 수 있습니다</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>이미지(JPG, PNG, WebP) 업로드 지원 (최대 20MB, 업로드 시 자동 리사이징)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>등록된 상품은 마감할인, 예약할인 설정에서 선택할 수 있습니다</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Product Forms */}
          <div className="lg:col-span-2">
            <div className="space-y-6">
              {products.map((product, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-[20px] font-semibold text-gray-800">
                        상품 {index + 1}
                      </h3>
                      {products.length > 1 && (
                        <button
                          onClick={() => handleRemoveProduct(index)}
                          className="text-red-500 hover:text-red-700 text-[16px] font-medium hover:bg-red-50 px-3 py-1 rounded-lg transition-colors"
                        >
                          삭제
                        </button>
                      )}
                    </div>
                    
                    <ProductFormCard
                      product={product}
                      onChange={(updatedProduct) => handleProductChange(index, updatedProduct)}
                      showCheckbox={false}
                      showDeleteButton={false}
                    />
                  </div>
                </div>
              ))}

              {/* 상품 추가 버튼 */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <button
                  onClick={handleAddProduct}
                  className="w-full py-6 border-2 border-dashed rounded-xl transition-all border-green-300 text-green-600 hover:border-green-500 hover:bg-green-50"
                >
                  <div className="flex flex-col items-center">
                    <svg className="w-8 h-8 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span className="text-[18px] font-semibold">상품 추가</span>
                    <span className="text-[14px] text-gray-500 mt-1">{products.length}개 등록됨</span>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Summary & Actions */}
          <div className="space-y-6">
            {/* 등록 요약 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-[20px] font-semibold text-gray-800 mb-4">등록 요약</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[16px] text-gray-600">등록할 상품 수</span>
                  <span className="text-[18px] font-semibold text-gray-800">{products.length}개</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[16px] text-gray-600">완료된 상품</span>
                  <span className="text-[18px] font-semibold text-green-600">
                    {products.filter(p => p.name.trim() && p.originalPrice > 0 && p.stock > 0).length}개
                  </span>
                </div>
              </div>
            </div>

            {/* 상품 등록 팁 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-[20px] font-semibold text-gray-800 mb-4">등록 팁</h3>
              <div className="space-y-3 text-[14px] text-gray-600">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p>매력적인 상품명으로 고객의 관심을 끌어보세요</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p>고품질 이미지는 주문률을 크게 높입니다</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p>간단한 설명으로 상품의 특징을 알려주세요</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p>적절한 가격 설정으로 경쟁력을 높이세요</p>
                </div>
              </div>
            </div>

            {/* 저장 버튼 */}
            <div className="sticky top-6">
              <button
                onClick={handleSave}
                disabled={!canSave}
                className={`w-full py-4 rounded-xl font-semibold text-[18px] transition-all ${
                  canSave
                    ? 'bg-green-500 text-white hover:bg-green-600 shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {saving ? '등록 중...' : '상품 등록'}
              </button>

              {!canSave && !saving && (
                <p className="text-center text-[14px] text-gray-500 mt-3">
                  모든 상품 정보를 입력해주세요
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </DesktopSellerPageContainer>
  );
}
